import { CommonModule } from '@angular/common';
import { Component, DestroyRef, inject, type OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import {
  Observable,
  Subject,
  concat,
  finalize,
  interval,
  of,
  switchMap,
  takeUntil,
  takeWhile,
  tap,
  throwError,
  timer,
} from 'rxjs';

import { ExportProgressStatus } from '@site-mate/dashpivot-shared-library';

import { PercentageBasedDownloaderData } from 'app/shared/model/downloader/percentage-based-downloader-data.model';
import { ExportResponse } from 'app/shared/model/export-response.model';
import { DownloaderService } from 'app/shared/service/downloader.service';
import { ErrorHandler } from 'app/shared/service/error-handler.service';
import { ToastrService } from 'app/shared/service/toastr.service';
import { UIComponentsModule } from 'app/ui-components/ui-components.module';

import { ListsService } from '../lists.service';

@Component({
  selector: 'cc-list-import-progress-modal',
  standalone: true,
  imports: [CommonModule, UIComponentsModule],
  templateUrl: './list-import-progress-modal.component.html',
  styleUrls: ['./list-import-progress-modal.component.scss'],
})
export class ListImportProgressModalComponent implements OnInit {
  progress = 0;
  displayedProgress = 0;

  sessionParameters = {} as ExportResponse;
  requestDelay = 1500;
  private destroyRef = inject(DestroyRef);
  private uploadCompleted = new Subject<void>();

  constructor(
    private readonly downloaderService: DownloaderService,
    private readonly errorHandler: ErrorHandler,
    private readonly toastrService: ToastrService,
    private readonly listsService: ListsService,
    public modal: NgbActiveModal,
  ) {}

  ngOnInit(): void {
    concat(this.pollImportSessionStatus(), this.checkAndCloseModal())
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({ error: (error) => this.handleError(error) });
  }

  private pollImportSessionStatus(): Observable<ExportResponse> {
    return timer(0, this.requestDelay).pipe(
      switchMap(() => this.downloaderService.getExportSession(this.sessionParameters.id)),
      tap((response) => this.updateProgress(response)),
      switchMap(this.handleExportStatusError.bind(this)),
      takeWhile((response) => this.exportInProgress(response), true),
    );
  }

  private checkAndCloseModal() {
    return interval(this.requestDelay).pipe(
      takeUntil(this.uploadCompleted),
      finalize(() => this.handleSuccess()),
    );
  }

  private exportInProgress(response: PercentageBasedDownloaderData) {
    return response && response.status === ExportProgressStatus.Processing;
  }

  private updateProgress(response: ExportResponse) {
    this.sessionParameters = response;
    const { processedObjects, totalObjects } = response;
    const progress = (Number(processedObjects) / Number(totalObjects)) * 100;
    this.progress = Number.isNaN(progress) ? 0 : Math.round(progress);
  }

  protected onProgressBarFull() {
    this.uploadCompleted.next();
    this.uploadCompleted.complete();
  }

  private handleSuccess() {
    this.toastrService.successByKey('listImported');
    this.listsService.refreshList();
    this.modal.close();
  }

  private handleError(error) {
    this.toastrService.errorByKey('listImportFailed');
    this.errorHandler.handle(error);
    this.modal.close();
  }

  private handleExportStatusError(response: PercentageBasedDownloaderData) {
    return response && response.status === ExportProgressStatus.Error
      ? throwError(() => new Error(JSON.stringify(response.error)))
      : of(response);
  }
}
